import { capitalize } from '@ardoq/common-helpers';
import { Select } from '@ardoq/select';
import { ReactNode } from 'react';
import { SearchContext } from '../types';
import {
  ConditionSelectWrapper,
  SearchContextSelectWrapper,
  SubqueryHeader,
} from './atoms';
import { BooleanOperator } from '@ardoq/api-types';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';

type SelectConditionBarProps = {
  condition: BooleanOperator;
  onConditionChange: (condition: BooleanOperator) => void;
  includeContextSelect?: boolean;
  contextSelectIsReadOnly?: boolean;
  searchContext?: SearchContext;
  onSearchContextChange?: (searchContext: SearchContext) => void;
  allowedSearchContexts?: SearchContext[];
  isReadOnly: boolean;
  isDisabled: boolean;
  clickIdPrefix?: string;
  topLevelLabelContent?: ReactNode;
  menuPortalTarget?: HTMLElement | null;
  i18nLocale?: I18nLocale;
};

const getLabelForCondition = (
  condition: BooleanOperator,
  i18nLocale: I18nLocale
): string =>
  condition === BooleanOperator.AND
    ? t(i18nLocale, 'queryBuilder:condition.all', 'all')
    : t(i18nLocale, 'queryBuilder:condition.any', 'any');

const getLabelForConditionInSentence = (
  condition: BooleanOperator,
  i18nLocale: I18nLocale
): string =>
  condition === BooleanOperator.AND
    ? t(i18nLocale, 'queryBuilder:condition.allInSentence', 'all')
    : t(i18nLocale, 'queryBuilder:condition.anyInSentence', 'any');

const getContextLabel = (
  context: SearchContext,
  i18nLocale: I18nLocale
): string => {
  if (context === SearchContext.COMPONENT) {
    return t(
      i18nLocale,
      'queryBuilder:conditionBar.contextSuffix.component',
      'components'
    );
  }
  if (context === SearchContext.REFERENCE) {
    return t(
      i18nLocale,
      'queryBuilder:conditionBar.contextSuffix.reference',
      'references'
    );
  }
  return t(
    i18nLocale,
    'queryBuilder:conditionBar.contextSuffix.workspace',
    'workspaces'
  );
};

export const SelectConditionBar = ({
  searchContext,
  onSearchContextChange,
  condition,
  onConditionChange,
  includeContextSelect,
  contextSelectIsReadOnly,
  allowedSearchContexts = [
    SearchContext.COMPONENT,
    SearchContext.REFERENCE,
    SearchContext.WORKSPACE,
  ],
  isReadOnly,
  isDisabled,
  clickIdPrefix,
  topLevelLabelContent,
  menuPortalTarget,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: SelectConditionBarProps) => {
  const contextOptions = allowedSearchContexts.map(context => ({
    label: getContextLabel(context, i18nLocale),
    value: context,
  }));

  if (includeContextSelect && searchContext && onSearchContextChange) {
    return isReadOnly ? (
      <SubqueryHeader $isDisabled={isDisabled}>
        {t(
          i18nLocale,
          'queryBuilder:conditionBar.withContextReadOnly',
          {
            condition: getLabelForConditionInSentence(condition, i18nLocale),
            context: getContextLabel(searchContext, i18nLocale),
          },
          'Search for {{condition}} of the {{context}} that match the following rules:'
        )}
      </SubqueryHeader>
    ) : (
      <SubqueryHeader $isDisabled={isDisabled}>
        {t(i18nLocale, 'queryBuilder:conditionBar.searchFor', 'Search for')}
        {contextSelectIsReadOnly ? (
          ` ${getContextLabel(searchContext, i18nLocale)} `
        ) : (
          <SearchContextSelectWrapper>
            <Select
              aria-label={t(
                i18nLocale,
                'queryBuilder:ariaLabel.searchContext',
                'Entity type to search for'
              )}
              isDisabled={isDisabled}
              value={searchContext}
              onValueChange={newSearchContext =>
                onSearchContextChange(newSearchContext as SearchContext)
              }
              options={contextOptions}
              clickId={
                clickIdPrefix ? `${clickIdPrefix}search-type` : undefined
              }
              dataTestId="search-type"
              menuPortalTarget={menuPortalTarget}
            />
          </SearchContextSelectWrapper>
        )}
        {t(i18nLocale, 'queryBuilder:conditionBar.thatMatch', 'that match')}
        <ConditionSelectWrapper>
          <Select
            aria-label={t(
              i18nLocale,
              'queryBuilder:ariaLabel.matchCondition',
              'Match all or any rules'
            )}
            isDisabled={isDisabled}
            value={condition}
            onValueChange={newCondition =>
              onConditionChange(newCondition as BooleanOperator)
            }
            options={[
              {
                label: getLabelForCondition(BooleanOperator.AND, i18nLocale),
                value: BooleanOperator.AND,
              },
              {
                label: getLabelForCondition(BooleanOperator.OR, i18nLocale),
                value: BooleanOperator.OR,
              },
            ]}
            clickId={clickIdPrefix ? `${clickIdPrefix}search-scope` : undefined}
            dataTestId="search-scope"
            menuPortalTarget={menuPortalTarget}
          />
        </ConditionSelectWrapper>
        {t(
          i18nLocale,
          'queryBuilder:conditionBar.ofTheFollowingRules',
          'of the following rules:'
        )}
      </SubqueryHeader>
    );
  }

  return isReadOnly ? (
    <SubqueryHeader $isDisabled={isDisabled}>
      {t(
        i18nLocale,
        'queryBuilder:conditionBar.topLevelReadOnly',
        { condition: capitalize(getLabelForCondition(condition, i18nLocale)) },
        '{{condition}} of the following rules:'
      )}
    </SubqueryHeader>
  ) : (
    <SubqueryHeader $isDisabled={isDisabled}>
      {topLevelLabelContent || null}
      <ConditionSelectWrapper className="first-condition">
        <Select
          isDisabled={isDisabled}
          value={condition}
          onValueChange={newCondition =>
            onConditionChange(newCondition as BooleanOperator)
          }
          options={[
            {
              label: t(
                i18nLocale,
                'queryBuilder:condition.allCapitalized',
                'All'
              ),
              value: BooleanOperator.AND,
            },
            {
              label: t(
                i18nLocale,
                'queryBuilder:condition.anyCapitalized',
                'Any'
              ),
              value: BooleanOperator.OR,
            },
          ]}
          dataTestId="first-condition-select"
          menuPortalTarget={menuPortalTarget}
        />
      </ConditionSelectWrapper>
      {t(
        i18nLocale,
        'queryBuilder:conditionBar.ofTheFollowingRules',
        'of the following rules:'
      )}
    </SubqueryHeader>
  );
};
